import { danbooruTagCategories } from "./danbooru-tag-types"
import type { BooruPost } from "./booru-api"
import { useSourceStore } from "./source-store"
import { useBooruCredentialsStore } from "./booru-credentials-store"
import { fetchE621Client } from "./e621-client"

const BOORU_VIDEO_EXT_REGEX = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv|gif|gifv)(\?|#|$)/i

export async function fetchBooruClient(
  sourceId: string,
  params: { tags: string; negativeTags?: string[]; limit?: number; page?: number },
): Promise<BooruPost[]> {
  const store = useSourceStore.getState()
  const config = store.getSource(sourceId)

  if (!config) {
    console.error(`[v0] Source ${sourceId} not found`)
    return []
  }

  console.log(`[v0] Client-side fetching from ${config.name}`)

  // Get user credentials from localStorage (ONLY source of credentials)
  const credentials = useBooruCredentialsStore.getState().getCredentials(sourceId)
  const apiKey = credentials?.apiKey
  const username = credentials?.username
  
  if (credentials) {
    console.log(`[v0] Using user credentials for ${sourceId}:`, { 
      hasUsername: !!username, 
      hasApiKey: !!apiKey 
    })
  }

  // e926 has its own response layout and shuts out our servers, so the visitor's browser asks it (see lib/e621-client.ts).
  if (config.apiType === "e926") {
    try {
      return await fetchE621Client(config, params, credentials)
    } catch (error) {
      console.error(`[v0] ${config.name} client-side fetch failed:`, error)
      return []
    }
  }

  try {
    const endpoint = config.endpoints.search
    const url = new URL(endpoint)

    // Add query parameters
    if (params.tags) url.searchParams.set("tags", params.tags)
    url.searchParams.set(config.limitParam, String(params.limit || 20))

    // Handle pagination
    if (params.page !== undefined && config.pagination) {
      const pageValue = params.page + config.pagination.startAt
      url.searchParams.set(config.pagination.param, String(pageValue))
    }

    // Add user credentials if available
    if ((config.auth.type === "api_key" || config.auth.type === "optional_api_key") && apiKey && config.auth.apiKeyParam) {
      url.searchParams.set(config.auth.apiKeyParam, apiKey)
      console.log(`[v0] Added API key to request for ${sourceId}`)
      
      if (username && config.auth.userIdParam) {
        url.searchParams.set(config.auth.userIdParam, username)
        console.log(`[v0] Added username to request for ${sourceId}`)
      }
    }

    // Build headers - use custom User-Agent (Danbooru requirement)
    const headers: HeadersInit = {
      Accept: "application/json, text/javascript, */*",
      "Accept-Language": "en-US,en;q=0.9",
    }

    // For Danbooru: Use custom User-Agent that identifies as a bot (NOT a browser)
    // Danbooru blocks bots that pretend to be browsers
    // Format: "BotName/Version Contact" or "Danbooru User #ID"
    if (username) {
      headers["User-Agent"] = `Goonru/1.0 (Danbooru User: ${username})`
      console.log(`[v0] Using custom User-Agent for ${sourceId}:`, headers["User-Agent"])
    } else {
      // Fallback to generic bot identifier (NOT browser-like)
      headers["User-Agent"] = "Goonru-API-Client/1.0 (https://github.com/goonru)"
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
      mode: "cors",
      credentials: "omit",
    })

    if (!response.ok) {
      console.error(`[v0] ${config.name} returned ${response.status}`)
      return []
    }

    const text = await response.text()
    let data: any

    try {
      data = JSON.parse(text)
    } catch (e) {
      console.error(`[v0] ${config.name} returned non-JSON response`)
      return []
    }

    // Parse response based on dataPath
    let posts = data
    if (config.dataPath) {
      posts = data[config.dataPath] || []
    } else if (!Array.isArray(data)) {
      posts = data.post || data.posts || []
    }

    if (!Array.isArray(posts)) {
      posts = []
    }

    // Map to BooruPost format
    return posts.map((post: any) => {
      const sampleKey = config.fieldMappings.sampleUrl
      const ratingKey = config.fieldMappings.rating
      const scoreKey = config.fieldMappings.score
      const sourceKey = config.fieldMappings.source
      const widthKey = config.fieldMappings.width
      const heightKey = config.fieldMappings.height

      const fileUrl = String(post[config.fieldMappings.fileUrl] || "")
      const postTags: string[] = (post[config.fieldMappings.tags] || "").split(config.fieldMappings.tagsDelimiter || " ")
      return {
        id: String(post[config.fieldMappings.id] || post.id),
        fileUrl,
        previewUrl: post[config.fieldMappings.previewUrl],
        sampleUrl: sampleKey ? post[sampleKey] || fileUrl : fileUrl,
        tags: postTags,
        // Danbooru sends the tags already split by type; keep that so post details can colour and group them.
        tagCategories: danbooruTagCategories(post, postTags),
        rating: ratingKey ? post[ratingKey] || "unknown" : "unknown",
        score: scoreKey ? post[scoreKey] || 0 : 0,
        source: sourceKey ? post[sourceKey] || null : null,
        width: widthKey ? post[widthKey] || 0 : 0,
        height: heightKey ? post[heightKey] || 0 : 0,
        site: sourceId,
        type: BOORU_VIDEO_EXT_REGEX.test(fileUrl) ? "video" : "image",
      }
    })
  } catch (error) {
    console.error(`[v0] ${config.name} client-side fetch failed:`, error)
    return []
  }
}
